import {useState} from 'react';
import {useTranslation} from 'react-i18next';

import {
  WorkspaceReservationStatus,
  showTimedFeedback,
  useAppDispatch,
  useAppSelector,
  useOverridableFeatureFlags,
  withAsyncThunkErrorHandling,
} from '@lib/store';
import {actions} from '@lib/store/src/data/timeslots/actions';
import {WorkspaceReservation} from '@lib/store/src/data/timeslots/apiTypes';
import {
  getOverlappingConfirmedWorkspaceReservations,
  getOverlappingWorkdays,
} from '@lib/store/src/data/timeslots/selectors';
import {useReservationDetails} from '@lib/store/src/ui/hooks/timeslots/useReservationDetails';
import {useConfirmationDialog} from './useConfirmationDialog';
import {timeLabel} from '@lib/store/src/data/timeslots/dateTimeHelpers';
import {isOfficeWorkday} from '@lib/store/src/data/timeslots/utils';
import {format} from 'date-fns';

type ReturnType = {
  isSaving: boolean;
  onAcceptInvitation: (onCompleted?: () => void) => void;
  onDeclineInvitation: (onCompleted?: () => void) => void;
};

export const useAcceptDeclineWorkspaceInvitation = (workspaceReservation: WorkspaceReservation): ReturnType => {
  const features = useOverridableFeatureFlags();
  const {confirmationDialog} = useConfirmationDialog();
  const {t} = useTranslation();
  const dispatch = useAppDispatch();

  const [isSaving, setIsSaving] = useState(false);

  const details = useReservationDetails(workspaceReservation);
  const currentMember = details.status === 'fulfilled' ? details.data.find((d) => d.isCurrentUser) : undefined;

  const overlappingWorkdays = useAppSelector(
    getOverlappingWorkdays(workspaceReservation.localStart, workspaceReservation.localEnd),
  );
  const overlappingConfirmedReservations = useAppSelector(
    getOverlappingConfirmedWorkspaceReservations(workspaceReservation.localStart, workspaceReservation.localEnd),
  );

  const workday = overlappingWorkdays.at(0);

  const isWorkspaceInDifferentBuilding = isOfficeWorkday(workday)
    ? workday.buildingId !== workspaceReservation.workspace.buildingId
    : false;

  const workspaceReservationFitsInsideWorkday =
    workday !== undefined
      ? !isWorkspaceInDifferentBuilding &&
        workspaceReservation.localStart >= workday.localStart &&
        workspaceReservation.localEnd <= workday.localEnd
      : true;

  const handleUpdateGroupWorkspaceReservationStatus = async (
    reservationStatus: Extract<WorkspaceReservationStatus, 'InvitationDeclined' | 'Confirmed'>,
  ) => {
    setIsSaving(true);

    const isConfirming = reservationStatus === 'Confirmed';

    // Clear workday when the provided workspace reservation does not fit
    // Or the workspace invitation is in a different building than the existing workday.
    // We'll create a new workday in its place
    if (isConfirming) {
      if (workday && (!workspaceReservationFitsInsideWorkday || isWorkspaceInDifferentBuilding)) {
        await dispatch(withAsyncThunkErrorHandling(() => actions.cancelWorkdayIncludingReservations({workday})));
      }
    }

    const action = isConfirming
      ? actions.acceptReservationInvitationWithAutoWorkday({workspaceReservation})
      : actions.declineReservationInvitation({workspaceReservation});

    const {success} = await dispatch(withAsyncThunkErrorHandling(() => action));

    if (success && isConfirming) {
      const getMessage = () => {
        if (currentMember?.isOrganizer) {
          return t('workplace:ReplacedGroupBookingMessage');
        }

        if (workspaceReservation) {
          return t('workplace:ReplacedBookingMessage');
        }

        return t('workplace:DeskHasBeenBooked');
      };

      const message = getMessage();

      dispatch(
        showTimedFeedback({
          type: 'success',
          message,
        }),
      );
    }

    setIsSaving(false);
  };

  const handleOnDeclineInvitation = async (onCompleted?: () => void) => {
    await handleUpdateGroupWorkspaceReservationStatus('InvitationDeclined');
    onCompleted?.();
  };

  const handleOnAcceptInvitation = async (onCompleted?: () => void) => {
    // Timeslots flow
    if (overlappingConfirmedReservations.length > 0) {
      await confirmationDialog(
        t('timeslots:ConflictingBookings'),
        t('timeslots:ConflictingBookingsDescription', {
          workspaceName: workspaceReservation.workspace.nodeName,
          timeslot: timeLabel(workspaceReservation.localStart, workspaceReservation.localEnd, t),
          date: format(new Date(workspaceReservation.localStart), 'EEEE dd MMMM'),
        }),
        'warning',
        t('translation:GotIt'),
      );

      return;
    }

    await handleUpdateGroupWorkspaceReservationStatus('Confirmed');
    onCompleted?.();
  };

  return {
    isSaving,
    onAcceptInvitation: handleOnAcceptInvitation,
    onDeclineInvitation: handleOnDeclineInvitation,
  };
};
